import { useCallback, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { v4 as uuidV4 } from "uuid";
import { storageDeleteFile } from "../../apis";
import { EquipmentAcPhotoI, StorageFileResponseI } from "../../interfaces";

export const MAX_PHOTOS = 3;

export interface UnifiedPhotoI {
  uuid: string;
  src: string;
  observation: string;
  order: number;
  id?: number;
  storageId?: number;
  isStorage?: boolean;
  isEmpty?: boolean;
}

interface UseEquipmentPhotosOptions {
  equipmentId?: number;
}

export function useEquipmentPhotos({
  equipmentId,
}: UseEquipmentPhotosOptions = {}) {
  const [photos, setPhotos] = useState<UnifiedPhotoI[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  /**
   * Inicializa el hook con los datos que vienen del backend.
   * Llámalo en handleGetData después de cargar el activo.
   *
   * @param legacyPhotos   dataEquipment.photos  (EquipmentAcPhotoI[])
   * @param storagePhotos  dataEquipment.storagePhotos (StorageFileResponseI[])
   */
  const initPhotos = useCallback(
    (
      legacyPhotos: EquipmentAcPhotoI[] = [],
      storagePhotos: (StorageFileResponseI & { url: string })[] = [],
    ) => {
      const unified: UnifiedPhotoI[] = [];

      for (const p of legacyPhotos) {
        if (p.src) {
          unified.push({
            uuid: p.uuid || uuidV4(),
            src: p.src,
            observation: p.observation || "",
            order: p.order || unified.length + 1,
            id: p.id,
          });
        }
      }

      for (const s of storagePhotos) {
        unified.push({
          uuid: uuidV4(),
          src: s.url,
          observation: "",
          order: unified.length + 1,
          storageId: s.id,
          isStorage: true,
        });
      }

      const slotsVacios = Math.max(0, MAX_PHOTOS - unified.length);
      for (let i = 0; i < slotsVacios; i++) {
        unified.push({
          uuid: uuidV4(),
          src: "",
          observation: "",
          order: unified.length + 1,
          isEmpty: true,
        });
      }

      setPhotos(unified);
    },
    [],
  );

  const initEmptyPhotos = useCallback(() => {
    const slots: UnifiedPhotoI[] = Array.from(
      { length: MAX_PHOTOS },
      (_, i) => ({
        uuid: uuidV4(),
        src: "",
        observation: "",
        order: i + 1,
        isEmpty: true,
      }),
    );
    setPhotos(slots);
  }, []);

  const updatePhoto = useCallback((updated: UnifiedPhotoI, uuid: string) => {
    setPhotos((prev) =>
      prev.map((p) => {
        if (p.uuid !== uuid) return p;

        if (!updated.src && p.id) {
          return {
            ...p,
            src: "",
            observation: "",
            isEmpty: true,
            id: undefined,
          };
        }

        return {
          ...p,
          src: updated.src,
          observation: updated.observation,
          isEmpty: !updated.src,
        };
      }),
    );
  }, []);

  const deleteStoragePhoto = useCallback(
    async (storageId: number, uuid: string) => {
      try {
        setDeletingId(storageId);
        await storageDeleteFile(storageId);

        setPhotos((prev) =>
          prev.map((p) =>
            p.uuid === uuid
              ? {
                  uuid: p.uuid,
                  src: "",
                  observation: "",
                  order: p.order,
                  isEmpty: true,
                }
              : p,
          ),
        );

        enqueueSnackbar("Foto eliminada con éxito", { variant: "success" });
      } catch (err: any) {
        enqueueSnackbar(err?.toString() ?? "Error al eliminar la foto", {
          variant: "error",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [],
  );

  const getLegacyPhotos = useCallback((): EquipmentAcPhotoI[] => {
    return photos
      .filter((p) => p.id !== undefined)
      .map((p) => ({
        id: p.id,
        uuid: p.uuid,
        src: p.src,
        observation: p.observation,
        order: p.order,
      }));
  }, [photos]);

  const getNewPhotos = useCallback((): EquipmentAcPhotoI[] => {
    return photos
      .filter((p) => !p.id && !p.isStorage && p.src?.startsWith("data:image"))
      .map((p) => ({
        uuid: p.uuid,
        src: p.src,
        observation: p.observation,
        order: p.order,
      }));
  }, [photos]);

  const getPhotosForSubmit = useCallback((): EquipmentAcPhotoI[] => {
    return [...getLegacyPhotos(), ...getNewPhotos()];
  }, [getLegacyPhotos, getNewPhotos]);

  const totalConFoto = photos.filter((p) => p.src && !p.isEmpty).length;
  const isFull = totalConFoto >= MAX_PHOTOS;

  return {
    photos,
    deletingId,
    initPhotos,
    initEmptyPhotos,
    updatePhoto,
    deleteStoragePhoto,
    getPhotosForSubmit,
    isFull,
    totalConFoto,
  };
}
